import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { creatorAPI } from '../api/endpoints';
import { formatXPUNT, formatDate, getUserId } from '../utils/helpers';
import { 
  FaArrowLeft, FaUsers, FaTicketAlt, FaChartLine, 
  FaWallet, FaPlus, FaEdit, FaCheck,
  FaTimes, FaStar, FaCrown, FaCopy, FaShare
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './CreatorDashboard.css';

const CreatorDashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [creatorInfo, setCreatorInfo] = useState({
    is_active: false,
    price: 0,
    type: 'weekly',
    total_subscribers: 0,
  });
  const [subscribers, setSubscribers] = useState([]);
  const [privateBets, setPrivateBets] = useState([]);
  const [earnings, setEarnings] = useState({ total: 0, thisMonth: 0 });
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [pricingForm, setPricingForm] = useState({
    price: 1,
    type: 'weekly',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [creatorLink, setCreatorLink] = useState('');

  // Get user ID from helpers
  const userId = getUserId();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!user?.punter_plus) {
      toast.error('You need Punter+ to access Creator Dashboard');
      navigate('/subscription');
      return;
    }
    
    // Set creator link with proper user ID
    if (userId) {
      const link = `${window.location.origin}/explore/creator/${userId}`;
      setCreatorLink(link);
    } else {
      console.warn('User ID not available. User object:', user);
      toast.error('Unable to get user ID. Please log in again.');
    }
    
    fetchCreatorData();
  }, [isAuthenticated, user]);

  const fetchCreatorData = async () => {
    setLoading(true);
    try {
      // Fetch creator info
      try {
        const infoRes = await creatorAPI.getInfo();
        if (infoRes.data) {
          setCreatorInfo({
            is_active: infoRes.data.is_active || false,
            price: infoRes.data.price || 0,
            type: infoRes.data.type || 'weekly',
            total_subscribers: infoRes.data.total_subscribers || 0,
          });
          if (infoRes.data.is_active) {
            setPricingForm({
              price: infoRes.data.price || 1,
              type: infoRes.data.type || 'weekly',
            });
          }
        }
      } catch (infoError) {
        console.log('Creator info not available yet:', infoError);
      }

      // Fetch subscribers - use userId
      if (userId) {
        try {
          const subscribersRes = await creatorAPI.getSubscribers(userId);
          setSubscribers(subscribersRes.data || []);
        } catch (subError) {
          console.log('No subscribers yet:', subError);
          setSubscribers([]);
        }

        // Fetch private bets - use userId
        try {
          const betsRes = await creatorAPI.getPrivateBets(userId, { per_page: 20 });
          setPrivateBets(betsRes.data?.data || []);
        } catch (betsError) {
          console.log('No private bets yet:', betsError);
          setPrivateBets([]);
        }
      }

      // Fetch earnings
      try {
        const earningsRes = await creatorAPI.getEarnings();
        setEarnings(earningsRes.data || { total: 0, thisMonth: 0 });
      } catch (earningsError) {
        console.log('No earnings yet:', earningsError);
        setEarnings({ total: 0, thisMonth: 0 });
      }

    } catch (error) {
      console.error('Failed to fetch creator data:', error);
      toast.error('Failed to load creator dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubscription = async () => {
    if (!pricingForm.price || pricingForm.price <= 0) {
      toast.error('Please set a valid price');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await creatorAPI.create({
        price: pricingForm.price,
        type: pricingForm.type,
      });
      if (response.data && response.data.success) {
        toast.success(response.data.message || 'Creator subscription activated! 🎉');
        setShowPricingModal(false);
        await fetchCreatorData();
      } else {
        toast.error(response.data?.message || 'Failed to activate subscription');
      }
    } catch (error) {
      console.error('Create subscription error:', error);
      toast.error(error.response?.data?.message || 'Failed to activate subscription');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdatePricing = async () => {
    if (!pricingForm.price || pricingForm.price <= 0) {
      toast.error('Please set a valid price');
      return;
    }

    setIsSubmitting(true);
    try {
      await creatorAPI.updatePricing(userId, {
        price: pricingForm.price,
        type: pricingForm.type,
      });
      toast.success('Pricing updated successfully!');
      setShowPricingModal(false);
      await fetchCreatorData();
    } catch (error) {
      console.error('Update pricing error:', error);
      toast.error(error.response?.data?.message || 'Failed to update pricing');
    } finally {
      setIsSubmitting(false);
    }
  };

  const copySubscriptionLink = () => {
    let link = creatorLink;
    
    if (!link && userId) {
      link = `${window.location.origin}/explore/creator/${userId}`;
      setCreatorLink(link);
    }
    
    if (!link) {
      toast.error('User ID not available. Please try again or contact support.');
      return;
    }
    
    if (navigator.clipboard) {
      navigator.clipboard.writeText(link).then(() => {
        toast.success('Creator link copied to clipboard!');
      }).catch(() => {
        copyToClipboard(link);
      });
    } else {
      copyToClipboard(link);
    }
  };

  const copyToClipboard = (text) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      toast.success('Creator link copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy link. Please copy manually.');
    }
    document.body.removeChild(textarea);
  };

  const shareCreatorLink = () => {
    let link = creatorLink;
    
    if (!link && userId) {
      link = `${window.location.origin}/explore/creator/${userId}`;
      setCreatorLink(link);
    }
    
    if (!link) {
      toast.error('User ID not available. Please try again.');
      return;
    }

    if (navigator.share) {
      navigator.share({
        title: `Subscribe to ${user?.display_name || user?.username || 'Creator'} on XPunters`,
        text: `Check out my creator page on XPunters! Subscribe for exclusive bet codes and content.`,
        url: link,
      }).catch(() => {});
    } else {
      copySubscriptionLink();
    }
  };

  if (loading) {
    return (
      <div className="creator-dashboard-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  const isActive = creatorInfo?.is_active || false;

  return (
    <div className="creator-dashboard">
      <button onClick={() => navigate(-1)} className="back-button">
        <FaArrowLeft /> Back
      </button>

      <div className="dashboard-header">
        <div>
          <h1>Creator Dashboard</h1>
          <p className="dashboard-subtitle">
            Manage your creator subscription and content
          </p>
        </div>
        {!isActive ? (
          <button 
            className="btn btn-primary"
            onClick={() => setShowPricingModal(true)}
          >
            <FaPlus /> Activate Creator Mode
          </button>
        ) : (
          <button 
            className="btn btn-secondary"
            onClick={() => setShowPricingModal(true)}
          >
            <FaEdit /> Edit Pricing
          </button>
        )}
      </div>

      {/* Status Card */}
      <div className={`status-card ${isActive ? 'active' : 'inactive'}`}>
        <div className="status-icon">
          {isActive ? <FaCrown className="active" /> : <FaStar />}
        </div>
        <div className="status-info">
          <h3>Creator Status</h3>
          <p className="status-badge">
            {isActive ? (
              <span className="badge active">✅ Active</span>
            ) : (
              <span className="badge inactive">❌ Inactive</span>
            )}
          </p>
          {isActive && (
            <div className="status-details">
              <span>Price: {formatXPUNT(creatorInfo.price)} / {creatorInfo.type}</span>
              <span>Subscribers: {creatorInfo.total_subscribers || 0}</span>
            </div>
          )}
        </div>
        {isActive && (
          <div className="creator-link-actions">
            <button onClick={copySubscriptionLink} className="copy-link-btn" title="Copy link">
              <FaCopy /> Copy Link
            </button>
            <button onClick={shareCreatorLink} className="share-link-btn" title="Share link">
              <FaShare /> Share
            </button>
          </div>
        )}
      </div>

      {/* Stats Grid */}
      {isActive && (
        <div className="stats-grid">
          <div className="stat-card">
            <FaUsers className="stat-icon" />
            <div>
              <span className="stat-value">{subscribers.length}</span>
              <span className="stat-label">Subscribers</span>
            </div>
          </div>
          <div className="stat-card">
            <FaTicketAlt className="stat-icon" />
            <div>
              <span className="stat-value">{privateBets.length}</span>
              <span className="stat-label">Private Bets</span>
            </div>
          </div>
          <div className="stat-card">
            <FaWallet className="stat-icon" />
            <div>
              <span className="stat-value">{formatXPUNT(earnings.total)}</span>
              <span className="stat-label">Total Earnings</span>
            </div>
          </div>
          <div className="stat-card">
            <FaChartLine className="stat-icon" />
            <div>
              <span className="stat-value">{formatXPUNT(earnings.thisMonth)}</span>
              <span className="stat-label">This Month</span>
            </div>
          </div>
        </div>
      )}

      {/* Two Column Layout */}
      <div className="dashboard-grid">
        {/* Private Bets */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3><FaTicketAlt /> Private Bet Codes</h3>
            <Link to="/bet-codes/create" className="btn btn-primary btn-sm">
              <FaPlus /> Create
            </Link>
          </div>
          {privateBets.length === 0 ? (
            <div className="empty-state-small">
              <p>No private bets created yet</p>
            </div>
          ) : (
            <div className="private-bets-list">
              {privateBets.slice(0, 5).map((bet) => (
                <Link to={`/bet-codes/${bet.id}`} key={bet.id} className="bet-item">
                  <div className="bet-info">
                    <span className="bet-title">{bet.title}</span>
                    <span className="bet-code">{bet.code}</span>
                  </div>
                  <span className="bet-views">{bet.views || 0} views</span>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Subscribers */}
        <div className="dashboard-card">
          <div className="card-header">
            <h3><FaUsers /> Subscribers</h3>
          </div>
          {subscribers.length === 0 ? (
            <div className="empty-state-small">
              <p>No subscribers yet</p>
              <small>Share your creator link to get subscribers</small>
            </div>
          ) : (
            <div className="subscribers-list">
              {subscribers.slice(0, 5).map((sub) => (
                <div key={sub.id} className="subscriber-item">
                  <img 
                    src={sub.avatar || '/default-avatar.png'} 
                    alt={sub.name}
                    className="subscriber-avatar"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="subscriber-info">
                    <span className="subscriber-name">{sub.name}</span>
                    <span className="subscriber-since">
                      Since {formatDate(sub.joined_at)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Pricing Modal */}
      {showPricingModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{isActive ? 'Update Pricing' : 'Activate Creator Mode'}</h2>
              <button 
                className="close-btn"
                onClick={() => setShowPricingModal(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-description">
                {isActive 
                  ? 'Update your subscription pricing. Existing subscribers will not be affected.'
                  : 'Set up your creator subscription to start earning from your content.'
                }
              </p>
              <div className="form-group">
                <label>Subscription Price ($XPUNT)</label>
                <input
                  type="number"
                  min="0.5"
                  step="0.5"
                  value={pricingForm.price}
                  onChange={(e) => setPricingForm({ ...pricingForm, price: parseFloat(e.target.value) || 0 })}
                  placeholder="Enter price in $XPUNT"
                />
                <small className="field-hint">Minimum price: 0.5 $XPUNT</small>
              </div>
              <div className="form-group">
                <label>Subscription Type</label>
                <select
                  value={pricingForm.type}
                  onChange={(e) => setPricingForm({ ...pricingForm, type: e.target.value })}
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div className="pricing-preview">
                <div className="preview-item">
                  <span>Earning per subscriber</span>
                  <span>{formatXPUNT(pricingForm.price)} / {pricingForm.type}</span>
                </div>
                <div className="preview-item">
                  <span>Admin fee (15%)</span>
                  <span>{formatXPUNT(pricingForm.price * 0.15)}</span>
                </div>
                <div className="preview-item highlight">
                  <span>Your earning per subscriber</span>
                  <span>{formatXPUNT(pricingForm.price * 0.85)}</span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowPricingModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={isActive ? handleUpdatePricing : handleCreateSubscription}
                disabled={isSubmitting || !pricingForm.price}
              >
                {isSubmitting ? (
                  <LoadingSpinner size="small" />
                ) : (
                  isActive ? 'Update Pricing' : 'Activate Creator Mode'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatorDashboard;